import React from "react";
import "./CaseStudy.css";
import { useNavigate } from "react-router-dom";

export default function JourneyMapReport() {
  const navigate = useNavigate();

  return (
    <div className="case-page">

      <button className="case-back" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 11.5L2.5 7.5M2.5 7.5L6.5 3.5M2.5 7.5H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Projects
      </button>

      <div className="case-hero">
        <h1>User Journey <em>Map Report</em></h1>
        <p className="case-hero-intro">
          A usability study of the MyFitnessPal app, mapping a user's emotional journey through nutrition goal-setting to surface three key pain points and deliver actionable design recommendations.
        </p>
        <div className="case-meta-inline">
          <span><strong>Role</strong>UX Researcher</span>
          <span><strong>Tools</strong>Figma, FigJam</span>
          <span><strong>Type</strong>Journey Map</span>
          <span><strong>Context</strong>Arizona State University</span>
        </div>
      </div>

      <div className="case-hero-img has-image">
        <img src="/images/mfpCoverHero.png" alt="MyFitnessPal Journey Map Report" />
      </div>

      <div className="case-body">

        {/* ── 01 Introduction ── */}
        <div className="case-section">
          <span className="case-section-num">01 — Introduction</span>
          <h2>Project <em>Introduction</em></h2>
          <p>This report details a user journey analysis conducted on the MyFitnessPal application. MyFitnessPal is a well-known and widely used health app that allows users to track their nutrition, exercise, and weight loss goals. Despite the app providing complete health management, it also presents usability challenges that can affect the user's experience.</p>
          <p>This report aims to identify specific pain points encountered by a user attempting to adjust their nutrition goals and track their progress on MyFitnessPal. I will also propose actionable recommendations for improvement based on established UX principles.</p>
          <p>The following task was designed to obtain user behavior and challenges related to nutrition goal management in MyFitnessPal.</p>
        </div>

        <div className="case-divider" />

        {/* ── 02 Task ── */}
        <div className="case-section">
          <span className="case-section-num">02 — Task</span>
          <h2>Task Scenario <em>&amp; Steps</em></h2>

          <h3>Task Scenario</h3>
          <p>Kelly is a busy working mom trying to start her weight loss journey. She recently started tracking her food intake to lose weight and wants to increase her protein intake. She needs to update her protein goal in MyFitnessPal so she can keep track of her progress with reaching her daily target.</p>

          <h3>Task Steps</h3>
          <ol>
            <li>Update the default macro goals in MyFitnessPal to increase the protein target.</li>
            <li>Locate where to view daily protein intake to track progress with the updated goal.</li>
          </ol>
        </div>

        <div className="case-divider" />

        {/* ── 03 Methodology ── */}
        <div className="case-section">
          <span className="case-section-num">03 — Methodology</span>
          <h2>Research <em>Methodology</em></h2>
          <p>To understand the user's interaction in MyFitnessPal for the specific task, a quantitative usability testing approach was used.</p>
          <ol>
            <li><strong>App and Task Identification:</strong> MyFitnessPal was selected due to personal observations of difficulties with specific task flows such as customizing nutrition goals.</li>
            <li><strong>Scenario and Task Definition:</strong> A realistic task scenario and two-part task were created to simulate a common user need.</li>
            <li><strong>Participant Recruitment:</strong> One participant was recruited to perform the task.</li>
            <li><strong>Think-Aloud Protocol:</strong> The participant was instructed to verbalize their thoughts, actions, and feelings throughout the task. This provided insights into their mental model, expectations, and emotions they felt.</li>
            <li><strong>Post-Task Interview:</strong> Once the task was completed, a post-test interview was conducted to gain additional qualitative feedback on their overall experience, such as difficulties and suggestions for improvement.</li>
          </ol>
        </div>

        <div className="case-divider" />

        {/* ── 04 Journey Map ── */}
        <div className="case-section">
          <span className="case-section-num">04 — Journey Map</span>
          <h2>Pain Points <em>&amp; Findings</em></h2>
          <p>Based on the task scenario and usability session, the user's journey through MyFitnessPal was charted, focusing on phases of goal adjustments and progress tracking. This journey map illustrates the user's path, their emotional state, and touchpoints.</p>

          <h3>Pain Point 1: Discoverability of Goal Settings</h3>
          <p><strong>Description:</strong> Upon opening the app, the user immediately began searching for the option to change their macro goal. Their initial attempts involved scrolling through the dashboard and tapping the top-left corner, suggesting they were expecting a "settings" or "goals" button in a common area. It took a moment, but the user eventually located the "more" tab (hamburger menu in the bottom-right corner) which then led them to the "goals" section. This indicates that the primary path to modify goals is not immediately intuitive or easy to find when first opening the app.</p>
          <p><strong>User Emotion:</strong> Initially neutral, then quickly shifted to confusion and some frustration due to inability to find the desired functionality.</p>
          <p><strong>Insight:</strong> The current placement of the "goals section" may not align with common user mental models of where to find personal settings.</p>

          <h3>Pain Point 2: Complexity of Macro Percentage Adjustment</h3>
          <p><strong>Description:</strong> After successfully navigating to the macro adjustment screen and increasing the protein percentage, the user pressed the "check" button to save, but the changes did not apply. MyFitnessPal requires all macronutrient percentage sums to equal 100%, which is not clearly explained when adjusting the goals. The user eventually figured out they needed to adjust another macro in order to make the total 100% and save the changes. This emphasizes the clear lack of guidance or error prevention in the interface.</p>
          <p><strong>User Emotion:</strong> Visible frustration and confusion when changes did not apply as expected, followed by a moment of realization.</p>
          <p><strong>Insight:</strong> The interaction for adjusting specific macro percentages is not intuitive. The lack of immediate explicit feedback when the 100% sum is not met creates a significant barrier to task completion and increases user frustrations.</p>

          {/* Strongest pain point from the report — swap if a better quote emerges from your full write-up */}
          <div className="case-callout">"The lack of immediate explicit feedback when the 100% sum is not met creates a significant barrier to task completion — a core failure of error prevention."</div>

          <h3>Pain Point 3: Discoverability of Daily Nutrient Summary</h3>
          <p><strong>Description:</strong> Following the successful update of the protein goal, the user returned to the dashboard page. They scanned the main page again but didn't immediately find where to locate the updated goal. Their next action was to tap the calories content card, then navigated to a separate "Nutrients" and "Macros" tab to see the view. This suggests that the daily nutrition tracker is not immediately present or directly linked in a prominent section.</p>
          <p><strong>User Emotion:</strong> Mild annoyance followed by relief once finding the desired information.</p>
          <p><strong>Insight:</strong> Daily progress metrics like the macronutrient breakdown are not easily discoverable and require several taps that are not intuitive to users who want to quickly see progress updates.</p>

          <div className="case-img">
            <img src="/images/mfpJourney.png" alt="MyFitnessPal User Journey Map diagram" />
          </div>
        </div>

        <div className="case-divider" />

        {/* ── 05 Conclusion ── */}
        <div className="case-section">
          <span className="case-section-num">05 — Conclusion</span>
          <h2>Final <em>Thoughts</em></h2>
          <p>User journey mapping proved to be a valuable tool to analyze the experience of adjusting and tracking nutrition goals in the MyFitnessPal app. Being able to visualize the user's path, emotions, and interactions helped identify clear pain points, specifically the discoverability of the goal settings, macro adjustments, and hidden nutrient summary. Addressing these usability issues has the potential to significantly improve the user experience of key task flows and empower users like Kelly to effectively manage their dietary goals. By creating more intuitive and efficient interactions, MyFitnessPal can solidify its position as a truly user-centric health and wellness app.</p>
        </div>

        <div className="case-divider" />

        {/* ── 06 Research Snapshot ── */}
        <div className="case-section">
          <span className="case-section-num">06 — At a Glance</span>
          <h2>Research <em>Snapshot</em></h2>
          <div className="outcome-grid">
            <div className="outcome-cell">
              {/* 1 participant confirmed in the methodology section */}
              <div className="outcome-number">1</div>
              <div className="outcome-desc">Usability testing session conducted with think-aloud protocol</div>
            </div>
            <div className="outcome-cell">
              {/* 2 task steps defined in the task scenario */}
              <div className="outcome-number">2</div>
              <div className="outcome-desc">Task scenarios mapped end-to-end across the app</div>
            </div>
            <div className="outcome-cell">
              {/* 3 pain points explicitly identified in the journey map */}
              <div className="outcome-number">3</div>
              <div className="outcome-desc">Pain points and opportunity areas identified</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">UX</div>
              <div className="outcome-desc">Actionable recommendations delivered to stakeholders</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
